import CrownIcon from "@/assets/CrownIcon";
import SmartphoneConnected from "@/assets/SmartphoneConnected";
import SmartphoneDisonnected from "@/assets/SmartphoneDisonnected";
import useGroups from "@/hooks/useGroups";
import useRefDimensions from "@/hooks/useRefDimensions";
import useTraces from "@/hooks/useTraces";
import useUsers from "@/hooks/useUsers";
import useGlobalStore from "@/services/store/useGlobalStore";
import useMapMode from "@/services/store/useMapMode";
import { DEFAULT_MISSION_STATUS, MAP_MISSION_STATUS, MAP_MISSION_TYPE } from "@/utils/constants/corpus";
import ContextMenu from "@components/ContextMenu/ContextMenu";
import StyledCheckbox from "@components/StyledCheckbox";
import GpsNotFixedIcon from "@mui/icons-material/GpsNotFixed";
import GpsOffIcon from "@mui/icons-material/GpsOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import RoomIcon from "@mui/icons-material/Room";
import RouteIcon from "@mui/icons-material/Route";
import FastForwardIcon from "@mui/icons-material/FastForward";
import { Box, IconButton, Snackbar, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import UserTraces from "./component/UserTraces";
import SkipPointPopup from "./component/SkipPointPopup";
import MessageService from "@/services/api/MessageService";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

interface IUser {
  user: UserWithColor;
  isSelected: boolean;
}

const User: React.FC<IUser> = ({ user, isSelected }) => {
  const theme: ThemeType = useTheme();
  const ref = useRef(null);

  const { height } = useRefDimensions(ref);

  const { users, setGPSValue } = useUsers();
  const { updateGroupLeader, groupsById } = useGroups();
  const { deleteAllTraces } = useTraces();
  const map = useMapMode((state) => state.map);

  const { selectUser, removeUserFromSelectedUsers } = useGlobalStore((state) => state);
  const traces = useGlobalStore((state) => state.traces);

  const storeUser = users.find((storeUser) => user.id === storeUser.id);

  const [userTracesOpen, setUserTracesOpen] = useState(false);
  const [userSkipPointOpen, setUserSkipPointOpen] = useState(false);

  // User actions states and variables
  const [locateErrorSnackBarOpen, setLocateErrorSnackBarOpen] = useState(false);

  const handleCheckboxClick = () => {
    if (isSelected) {
      removeUserFromSelectedUsers(user.id);
    } else {
      selectUser(user.id);
    }
  };

  const handleCrownClick = () => {
    updateGroupLeader(user.group.id, user.alias ?? user.username);
  };

  const getContextMenuOptions = () => {
    const contextMenuOptions = [
      {
        label: "Réinitialiser",
        onClick: () => deleteAllTraces(traces.filter((t) => t.userId === user.id)),
        icon: <RefreshIcon />,
      },
      {
        label: "Localiser",
        onClick: () => {
          const userTraces = traces.filter((t) => t.userId === user.id);
          const lastTrace = userTraces.at(-1);
          const lastPoint = lastTrace && lastTrace.points.length > 0 ? lastTrace.points.at(-1) : null;
          const coordinates = storeUser?.coordinates;

          if (lastTrace && lastPoint) {
            map.setView([lastPoint.x, lastPoint.y], 20);
          } else if (coordinates) {
            map.setView([coordinates.x, coordinates.y], 20);
          } else {
            setLocateErrorSnackBarOpen(true);
          }
        },
        icon: <RoomIcon />,
      },
      {
        label: "Traces",
        onClick: () => {
          setUserTracesOpen(true);
        },
        icon: <RouteIcon />,
      },
      {
        label: "Passer un point",
        onClick: () => {
          MessageService.skipPoints(user.id, 1);
        },
        icon: <PlayArrowIcon />,
        closeOnClick: false,
      },
      {
        label: "Passer plusieurs points",
        onClick: () => {
          setUserSkipPointOpen(true);
        },
        icon: <FastForwardIcon />,
      },
    ];
    // Add the possibility to enable / disable GPS when the group doesn't have a mission that need this feature
    if (
      (storeUser && !storeUser.group) ||
      (storeUser &&
        groupsById[storeUser.group.id].users.filter(
          (u) =>
            u.mission?.status === "ONGOING" &&
            (u.mission?.type === "FOLLOW_LEADER" || u.mission?.type === "TEAM_POSITION")
        ).length === 0)
    ) {
      contextMenuOptions.push({
        label: storeUser.isGPSEnabled ? "Désactiver le GPS" : "Activer le GPS",
        onClick: () => setGPSValue(storeUser.id, !storeUser.isGPSEnabled),
        icon: storeUser.isGPSEnabled ? <GpsOffIcon /> : <GpsNotFixedIcon />,
      });
    }
    return contextMenuOptions;
  };

  const contextMenuOptions = getContextMenuOptions();

  const missionNameAndStatus = `${user.mission ? `${MAP_MISSION_TYPE[user.mission.type]} - ` : ""} ${user.mission ? MAP_MISSION_STATUS[user.mission.status] : DEFAULT_MISSION_STATUS}`;

  // Alert button: animate card when a new SHORT_PRESS point arrives
  const lastAlertIdRef = useRef<number | null>(null);
  const [isAlertActive, setIsAlertActive] = useState(false);

  const lastShortPressPoint = useMemo(() => {
    const userTraces = traces.filter((t) => t.userId === user.id);
    const allPoints = userTraces.flatMap((t) => t.points);
    return [...allPoints].reverse().find((p) => p.buttonStatus === "SHORT_PRESS") ?? null;
  }, [traces, user.id]);

  useEffect(() => {
    if (lastShortPressPoint && lastShortPressPoint.id !== lastAlertIdRef.current) {
      lastAlertIdRef.current = lastShortPressPoint.id;
      setIsAlertActive(true);
      const timer = setTimeout(() => setIsAlertActive(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastShortPressPoint?.id]);

  return (
    <>
      <Stack
        ref={ref}
        direction="row"
        width="100%"
        sx={{
          background: "white",
          borderRadius: 1,
          overflow: "hidden",
          boxShadow: isAlertActive ? "0px 0px 30px 5px #FF0000" : "none",
          transition: "box-shadow 0.3s ease",
        }}
        alignItems="center"
        justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          <Box height={height} width={10} sx={{ background: user.color }} />
          <StyledCheckbox checked={isSelected} onChange={handleCheckboxClick} />

          <IconButton disabled={!user.group} onClick={handleCrownClick}>
            <CrownIcon color={user.leader ? user.color : theme.palette.disabled.main} />
          </IconButton>

          <Stack direction="column" sx={{ pr: 1, pl: 1 }}>
            <Typography>{user.alias ?? user.username}</Typography>
            <Tooltip title={missionNameAndStatus}>
              <Typography variant="body2" sx={{ fontStyle: "italic", maxWidth: 135 }} noWrap>
                {missionNameAndStatus}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack direction="row">
          <Stack direction="row" alignItems="center" sx={{ margin: "0" }}>
            {user.status === "CONNECTED" ? (
              <SmartphoneConnected color={theme.palette.success.main} />
            ) : (
              <SmartphoneDisonnected color={theme.palette.error.main} />
            )}
            {user.isGPSEnabled && user.status === "CONNECTED" ? (
              <GpsNotFixedIcon sx={{ color: theme.palette.success.main, paddingLeft: "10px" }} />
            ) : (
              <></>
            )}
          </Stack>
          <ContextMenu options={contextMenuOptions} />
        </Stack>
      </Stack>
      {userTracesOpen &&
        createPortal(<UserTraces user={user} onClose={() => setUserTracesOpen(false)} />, document.body)}
      <SkipPointPopup user={user} open={userSkipPointOpen} onClose={() => setUserSkipPointOpen(false)} />
      {createPortal(
        <Snackbar
          open={locateErrorSnackBarOpen}
          autoHideDuration={5000}
          onClose={() => setLocateErrorSnackBarOpen(false)}
          message={`Impossible de localiser ${user.alias ?? user.username} (pas de point associé)`}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ zIndex: 9999 }}
        />,
        document.body
      )}
    </>
  );
};

export default User;
